import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { CardMedia, Tile, TileContent, TileMedia } from '@one-thd/sui-atomic-components';
import { useImpression } from '@thd-olt-component-react/impression';
import propTypes from 'prop-types';

const {
  bool, func, number, oneOf, shape, string
} = propTypes;

export const MiniSwatch = ({
  swatchGuid,
  value,
  isSelected,
  enableHover,
  onClick,
  onMouseEnter,
  onMouseLeave,
  swatchItemId,
  index,
  componentImpressionData,
  isImpressionsRequired,
  wrapperClasses,
  swatchSize,
  withoutTileBorder,
  ariaLabel,
  swatchName
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClickedMobile, setIsClickedMobile] = useState(false);

  const mouseEnter = useCallback((event) => {
    if (enableHover) {
      setIsHovered(true);
      onMouseEnter(event);
    }
  }, [enableHover, onMouseEnter]);

  const mouseLeave = useCallback((event) => {
    if (enableHover) {
      setIsHovered(false);
      onMouseLeave(event);
    }
  }, [enableHover, onMouseLeave]);

  const onClickMobile = () => {
    if (!enableHover) {
      setIsClickedMobile(true);
    }
  };

  const impressionProviderContextData = useImpression({
    data: {
      id: swatchItemId || '',
      name: componentImpressionData?.name || '',
      component: componentImpressionData?.component || '',
      position: componentImpressionData?.position || 1,
      type: componentImpressionData?.type || '',
      category: 'ssku',
      anchorSku: componentImpressionData?.anchorSku || ''
    }
  });

  const shouldUseImpressionData = index !== 0 && isImpressionsRequired && (isHovered || isClickedMobile);
  const swatchRef = shouldUseImpressionData ? impressionProviderContextData.ref : null;
  const swatchClickId = shouldUseImpressionData ? impressionProviderContextData.clickID : null;

  const swatchCardClasses = classNames(
    `sui-w-${swatchSize} sui-h-${swatchSize}`,
    { 'sui-border-2 sui-border-solid sui-border-stronger hover:sui-bg-subtle focus-visible:sui-bg-subtle': isSelected },
    { 'sui-border-input-primary hover:sui-border-accent hover:sui-border-2 hover:sui-border-solid': !isSelected }
  );

  const SwatchBodyTile = () => (
    <Tile
      value={value}
      selected={isSelected}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
      onClick={(event) => {
        onClickMobile();
        onClick(event);
      }}
      data-testid="mini-swatch-body-tile"
      data-clickid={swatchClickId}
      aria-label={ariaLabel || `${swatchName || 'Swatch'} ${isSelected ? 'selected' : 'not selected'}`}
      ref={swatchRef}
    >
      <TileContent alignItems="center">
        <TileMedia
          alt={`${swatchName || 'Swatch'} color or pattern variation`}
          src={swatchGuid}
          height={String(swatchSize)}
          key={index}
        />
      </TileContent>
    </Tile>
  );

  const SwatchBodyCard = () => (
    <button
      type="button"
      data-testid="mini-swatch-body-card"
      data-clickid={swatchClickId}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
      onClick={(event) => {
        onClickMobile();
        onClick(event);
      }}
      className={swatchCardClasses}
      value={value}
      aria-pressed={isSelected}
      aria-label={ariaLabel || `${swatchName || 'Swatch'} ${isSelected ? 'selected' : 'not selected'}`}
      ref={swatchRef}
    >
      <CardMedia
        alt={`${swatchName || 'Swatch'} color or pattern variation`}
        src={swatchGuid}
        width="min"
        aspect="square"
        key={index}
      />
    </button>
  );

  return (
    <div data-testid="mini-swatch-wrapper" className={wrapperClasses}>
      {withoutTileBorder ? <SwatchBodyCard /> : <SwatchBodyTile />}
    </div>
  );
};

MiniSwatch.displayName = 'MiniSwatch';

MiniSwatch.propTypes = {
  swatchGuid: string.isRequired,
  value: string.isRequired,
  isSelected: bool.isRequired,
  enableHover: bool.isRequired,
  onClick: func.isRequired,
  onMouseEnter: func.isRequired,
  onMouseLeave: func.isRequired,
  swatchItemId: string,
  index: number,
  componentImpressionData: shape({
    name: string,
    component: string,
    position: number,
    type: string,
    anchorSku: string
  }),
  isImpressionsRequired: bool,
  wrapperClasses: string,
  /** Default '7'; See Stencil Design System, Spacing Tokens for available pixel values */
  swatchSize: oneOf(['4', '5', '6', '7', '8', '9']),
  withoutTileBorder: bool,
  ariaLabel: string,
  swatchName: string
};

MiniSwatch.defaultProps = {
  swatchItemId: null,
  index: 0,
  componentImpressionData: {
    name: '',
    component: '',
    position: 1,
    type: '',
    anchorSku: ''
  },
  isImpressionsRequired: false,
  wrapperClasses: null,
  swatchSize: '7',
  withoutTileBorder: false,
  ariaLabel: '',
  swatchName: ''
};
